/**
 * API Call Tracker for RUM
 *
 * Tracks the total number of API calls every 24 hours when isVesselOnCloud = true
 * Tracks separately for each IMO, sharing the same 24-hour period
 * Uses IndexedDB for persistence across sessions and offline periods
 * Integrates with service worker for offline event queuing
 */

import { faro } from './rum-utility'
import { VoyageImoModel } from '../models/voyage-imo-model'

const DB_NAME = 'ApiTrackingDB'
const STORE_NAME = 'api-call-metrics'
const DB_VERSION = 2

interface ApiCallMetrics {
    id: string // 'imo-{imoNumber}' for per-IMO tracking
    imo: string
    count: number
    totalBandwidthBytes: number // Total bytes sent + received
    requestBytesTotal: number // Total bytes sent in requests
    responseBytesTotal: number // Total bytes received in responses
    schedulesApisBytes: number // Total bytes for schedules API calls
    plansApisBytes: number // Total bytes for plans API calls
    weatherLayersBytes: number // Total bytes for weather layers API calls
    weatherForecastBytes: number // Total bytes for weather forecast API calls
    positionsApiBytes: number // Total bytes for positions API calls
    notificationsApiBytes: number // Total bytes for notifications API calls
    terminalsApiBytes: number // Total bytes for terminals API calls
    portMemosApiBytes: number // Total bytes for port memos API calls 
    mapsApiBytes: number // Total bytes for maps API calls 
    playForwardClicked: string // Number of times play forward clicked
    geofenceApiBytes: number // Total bytes for geofence API calls (optional)
    configApiBytes: number // Total bytes for config API calls (optional)
    performanceApiBytes: number // Total bytes for performance API calls (optional)
    threeMdsApiBytes: number // Total bytes for 3MDS API calls (optional)
    routeOptimiserApiBytes: number // Total bytes for route optimiser API calls (optional)
    insightHubApiBytes: number // Total bytes for insight hub API calls (optional)
    encTileserverApiBytes: number // Total bytes for ENC tileserver API calls (optional)
    onlineStatusApiBytes: number // Total bytes for online-status / vesselstatus API calls (optional)
}

interface GlobalTimingMetrics {
    id: string // 'global-timing'
    lastResetTimestamp: number
    lastPushTimestamp: number | null
}

const TRACKING_INTERVAL_MS = 24 * 60 * 60 * 1000 // 24 hours
const GLOBAL_TIMING_ID = 'global-timing'

class ApiCallTracker {
    private db: IDBDatabase | null = null
    private intervalId: number | null = null
    private isVesselOnCloud: boolean
    private initializationPromise: Promise<void> | null = null

    constructor() {
        this.isVesselOnCloud = (window as any).appenv?.vesselOnCloud ?? false
    }

    async initialize(): Promise<void> {
        if (this.initializationPromise) {
            return this.initializationPromise
        }

        if (!this.isVesselOnCloud) {
            this.initializationPromise = Promise.resolve()
            return this.initializationPromise
        }

        this.initializationPromise = this.performInitialization()
        return this.initializationPromise
    }

    private async performInitialization(): Promise<void> {
        try {
            await this.openDatabase()
            await this.ensureMetricsExist()
            this.startDailyPushInterval()
            await this.checkAndPushIfNeeded()
        } catch (error) {
            console.error('Failed to initialize API call tracker:', error)
            // Reset initialization promise to allow retry
            this.initializationPromise = null
            throw error
        }
    }

    private openDatabase(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION)

            request.onerror = () => {
                reject(new Error('Failed to open IndexedDB'))
            }

            request.onsuccess = () => {
                this.db = request.result
                resolve()
            }

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' })
                }
            }
        })
    }

    private async ensureMetricsExist(): Promise<void> {
        const timing = await this.getGlobalTiming()

        if (!timing) {
            const initialTiming: GlobalTimingMetrics = {
                id: GLOBAL_TIMING_ID,
                lastResetTimestamp: Date.now(),
                lastPushTimestamp: null,
            }
            await this.saveGlobalTiming(initialTiming)
            dispatchEvent(new Event('api-call-tracker-initialized'));
        }
    }

    private getGlobalTiming(): Promise<GlobalTimingMetrics | null> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readonly')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.get(GLOBAL_TIMING_ID)

            request.onsuccess = () => {
                resolve(request.result || null)
            }

            request.onerror = () => {
                reject(new Error('Failed to get global timing'))
            }
        })
    }

    private saveGlobalTiming(timing: GlobalTimingMetrics): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readwrite')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.put(timing)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to save global timing'))
            }
        })
    }

    private getMetricsForImo(imo: string): Promise<ApiCallMetrics | null> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const id = `imo-${imo}`
            const transaction = this.db.transaction(STORE_NAME, 'readonly')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.get(id)

            request.onsuccess = () => {
                resolve(request.result || null)
            }

            request.onerror = () => {
                reject(new Error('Failed to get metrics for IMO'))
            }
        })
    }

    private saveMetricsForImo(metrics: ApiCallMetrics): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readwrite')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.put(metrics)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = () => {
                reject(new Error('Failed to save metrics for IMO'))
            }
        })
    }

    private getAllImoMetrics(): Promise<ApiCallMetrics[]> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readonly')
            const store = transaction.objectStore(STORE_NAME)
            const request = store.getAll()

            request.onsuccess = () => {
                const allRecords = request.result || []
                // Filter out global timing record, only return IMO metrics
                const imoMetrics = allRecords.filter(
                    (record: any) => record.id && record.id.startsWith('imo-'),
                ) as ApiCallMetrics[]
                resolve(imoMetrics)
            }

            request.onerror = () => {
                reject(new Error('Failed to get all IMO metrics'))
            }
        })
    }

    async incrementCallCount(
        requestBytes: number = 0,
        responseBytes: number = 0,
        url: string = '',
    ): Promise<void> {
        if (!this.isVesselOnCloud) {
            return
        }

        // Ensure database is initialized
        if (!this.db) {
            try {
                await this.initialize()
            } catch (error) {
                console.error('Failed to initialize database for API tracking:', error)
                return
            }
        }

        try { 
            // Validate and sanitize byte values
            const validRequestBytes = isNaN(requestBytes) || requestBytes === undefined || requestBytes === null ? 0 : Number(requestBytes);
            const validResponseBytes = isNaN(responseBytes) || responseBytes === undefined || responseBytes === null ? 0 : Number(responseBytes);
            const totalBytes = validRequestBytes + validResponseBytes;

            // Get current IMO
            const currentImo = VoyageImoModel?.imo
            if (!currentImo) {
                console.warn('No IMO available, skipping API call tracking')
                return
            }

            const imoString = currentImo.toString()
            const id = `imo-${imoString}`

            // Use atomic read-modify-write transaction with retry
            await this.retryOperation(() => 
                this.atomicUpdateMetrics(id, imoString, {
                    count: 1,
                    requestBytes: validRequestBytes,
                    responseBytes: validResponseBytes,
                    totalBytes,
                    url
                })
            )

        } catch (error) {
            console.error('Failed to increment API call count after retries:', error)
        }
    }

    private async retryOperation<T>(operation: () => Promise<T>, maxRetries = 3): Promise<T | void> {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await operation()
            } catch (error) {
                if (attempt === maxRetries) {
                    throw error
                }
                // Wait briefly before retry
                await new Promise(resolve => setTimeout(resolve, attempt * 10))
            }
        }
    }

    private atomicUpdateMetrics(
        id: string, 
        imo: string, 
        increment: { count: number; requestBytes: number; responseBytes: number; totalBytes: number; url: string }
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'))
                return
            }

            const transaction = this.db.transaction(STORE_NAME, 'readwrite')
            const store = transaction.objectStore(STORE_NAME)

            transaction.oncomplete = () => resolve()
            transaction.onerror = () => reject(new Error('Atomic update transaction failed'))

            const getRequest = store.get(id)
            
            getRequest.onsuccess = () => {
                let metrics = getRequest.result

                if (!metrics) {
                    // Create new metrics entry
                    metrics = {
                        id,
                        imo,
                        count: 0,
                        totalBandwidthBytes: 0,
                        requestBytesTotal: 0,
                        responseBytesTotal: 0,
                        schedulesApisBytes: 0,
                        plansApisBytes: 0,
                        weatherLayersBytes: 0,
                        weatherForecastBytes: 0,
                        positionsApiBytes: 0,
                        notificationsApiBytes: 0,
                        terminalsApiBytes: 0,
                        portMemosApiBytes: 0,
                        mapsApiBytes: 0,
                        playForwardClicked: localStorage.getItem('play-clicked') || '0',
                        geofenceApiBytes: 0,
                        configApiBytes: 0,
                        performanceApiBytes: 0,
                        threeMdsApiBytes: 0,
                        routeOptimiserApiBytes: 0,
                        insightHubApiBytes: 0,
                        encTileserverApiBytes: 0,
                        onlineStatusApiBytes: 0,
                    }
                }

                // Sanitize existing values to prevent corruption
                metrics.count = Number(metrics.count) || 0
                metrics.totalBandwidthBytes = Number(metrics.totalBandwidthBytes) || 0
                metrics.requestBytesTotal = Number(metrics.requestBytesTotal) || 0
                metrics.responseBytesTotal = Number(metrics.responseBytesTotal) || 0
                metrics.schedulesApisBytes = Number(metrics.schedulesApisBytes) || 0
                metrics.plansApisBytes = Number(metrics.plansApisBytes) || 0
                metrics.weatherLayersBytes = Number(metrics.weatherLayersBytes) || 0
                metrics.weatherForecastBytes = Number(metrics.weatherForecastBytes) || 0
                metrics.positionsApiBytes = Number(metrics.positionsApiBytes) || 0
                metrics.notificationsApiBytes = Number(metrics.notificationsApiBytes) || 0
                metrics.terminalsApiBytes = Number(metrics.terminalsApiBytes) || 0
                metrics.portMemosApiBytes = Number(metrics.portMemosApiBytes) || 0
                metrics.mapsApiBytes = Number(metrics.mapsApiBytes) || 0
                metrics.geofenceApiBytes = Number(metrics.geofenceApiBytes) || 0
                metrics.configApiBytes = Number(metrics.configApiBytes) || 0
                metrics.performanceApiBytes = Number(metrics.performanceApiBytes) || 0
                metrics.threeMdsApiBytes = Number(metrics.threeMdsApiBytes) || 0
                metrics.routeOptimiserApiBytes = Number(metrics.routeOptimiserApiBytes) || 0
                metrics.insightHubApiBytes = Number(metrics.insightHubApiBytes) || 0
                metrics.encTileserverApiBytes = Number(metrics.encTileserverApiBytes) || 0
                metrics.onlineStatusApiBytes = Number(metrics.onlineStatusApiBytes) || 0

                // Apply atomic increments
                metrics.count += increment.count
                metrics.requestBytesTotal += increment.requestBytes
                metrics.responseBytesTotal += increment.responseBytes
                metrics.totalBandwidthBytes += increment.totalBytes

                // Update category-specific metrics
                this.updateCategoryMetrics(metrics, increment.url, increment.totalBytes)

                const putRequest = store.put(metrics)
                putRequest.onerror = () => reject(new Error('Failed to save updated metrics'))
            }

            getRequest.onerror = () => reject(new Error('Failed to read metrics for atomic update'))
        })
    }

    private updateCategoryMetrics(metrics: ApiCallMetrics, url: string, totalBytes: number): void {
        // Normalize URL for consistent matching
        const normalizedUrl = url.toLowerCase()
        let matched = false
        
        if (normalizedUrl.includes('schedulesapi')) {
            metrics.schedulesApisBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('voy-vvc-plansapi')) {
            metrics.plansApisBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('positionsapi')) {
            metrics.positionsApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('notificationsapi')) {
            metrics.notificationsApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('terminalsapi')) {
            metrics.terminalsApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('portmemos')) {
            metrics.portMemosApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('weather/api/v1/forecasts') || 
                   (normalizedUrl.includes('forecast') && normalizedUrl.includes('weather'))) {
            metrics.weatherForecastBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('weatherwms') || 
                   normalizedUrl.includes('weather/api') || 
                   normalizedUrl.includes('/kml/') ||
                   (normalizedUrl.includes('weather') && normalizedUrl.includes('/api/'))) {
            metrics.weatherLayersBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('tile.openstreetmap') || 
                   normalizedUrl.includes('openstreetmap') ||
                   normalizedUrl.includes('osm') ||
                   normalizedUrl.includes('/tiles/')) {
            metrics.mapsApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('geofenceapi')) {
            metrics.geofenceApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('configapi')) {
            metrics.configApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('performanceapi')) {
            metrics.performanceApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('3mds')) {
            metrics.threeMdsApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('routeoptimiserapi')) {
            metrics.routeOptimiserApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('insightshub-api')) {
            // Insight Hub API / feedback endpoints
            metrics.insightHubApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('enc-tileserver')) {
            // ENC tileserver requests
            metrics.encTileserverApiBytes += totalBytes
            matched = true
        } else if (normalizedUrl.includes('onlinestatusapi')) {
            metrics.onlineStatusApiBytes += totalBytes
            matched = true
        }
        
        // Track unmatched URLs for debugging (with size info for prioritization)
        if (!matched && totalBytes > 0) {
            const isVesselOnCloud = (window as any).appenv?.vesselOnCloud ?? false
            if (isVesselOnCloud) {
                // Store unmatched URL info for debugging (only in production to see real issues)
                console.log(`Unmatched API URL (${totalBytes} bytes): ${url}`)
            }
        }
    }

    private async checkAndPushIfNeeded(): Promise<void> {
        try {
            const timing = await this.getGlobalTiming()

            if (!timing) {
                return
            }

            const timeSinceReset = Date.now() - timing.lastResetTimestamp

            // If 24 hours have passed since last reset, push and reset
            if (timeSinceReset >= TRACKING_INTERVAL_MS) {
                await this.pushAllMetricsToRUM(timing)

                // Reset timing
                timing.lastResetTimestamp = Date.now()
                timing.lastPushTimestamp = Date.now()
                await this.saveGlobalTiming(timing)

                // Clear all IMO metrics after successful push
                await this.clearAllImoMetrics()
            }
        } catch (error) {
            console.error('Failed to check and push metrics:', error)
        }
    }

    private async pushAllMetricsToRUM(
        timing: GlobalTimingMetrics,
    ): Promise<void> {
        try {
            // Get all IMO metrics
            const allMetrics = await this.getAllImoMetrics()

            if (allMetrics.length === 0) {
                return
            }

            // Push individual event for each IMO
            for (const metrics of allMetrics) {
                // Calculate sum of category-wise metrics for validation (including newly tracked categories)
                const categorySum = metrics.schedulesApisBytes + metrics.plansApisBytes + 
                    metrics.weatherLayersBytes + metrics.weatherForecastBytes + 
                    metrics.positionsApiBytes + metrics.notificationsApiBytes + 
                    metrics.terminalsApiBytes + metrics.portMemosApiBytes + metrics.mapsApiBytes +
                    metrics.geofenceApiBytes + metrics.configApiBytes + metrics.performanceApiBytes + metrics.threeMdsApiBytes + metrics.routeOptimiserApiBytes +
                    metrics.insightHubApiBytes + metrics.encTileserverApiBytes + metrics.onlineStatusApiBytes;
                
                const totalBytes = metrics.totalBandwidthBytes
                const unmatchedBytes = totalBytes - categorySum

                const eventData = {
                    imo: `${parseInt(metrics.imo, 10)}`,
                    apiCallCount: `${metrics.count}`,
                    totalBandwidthBytes: `${metrics.totalBandwidthBytes}`,
                    totalBandwidthMB: `${parseFloat(
                        (metrics.totalBandwidthBytes / (1024 * 1024)).toFixed(2),
                    )}`,
                    requestBytesTotal: `${metrics.requestBytesTotal}`,
                    requestBytesMB: `${parseFloat(
                        (metrics.requestBytesTotal / (1024 * 1024)).toFixed(2),
                    )}`,
                    responseBytesTotal: `${metrics.responseBytesTotal}`,
                    responseBytesMB: `${parseFloat(
                        (metrics.responseBytesTotal / (1024 * 1024)).toFixed(2),
                    )}`,
                    trackingPeriodStart: `${new Date(
                        timing.lastResetTimestamp,
                    ).toISOString()}`,
                    trackingPeriodEnd: `${new Date().toISOString()}`,
                    timeSinceLastReset: `${
                        Date.now() - timing.lastResetTimestamp
                    }`,
                    schedulesApisBytes: `${parseFloat(
                        (metrics.schedulesApisBytes / (1024 * 1024)).toFixed(2))}`,
                    plansApisBytes: `${parseFloat(
                        (metrics.plansApisBytes / (1024 * 1024)).toFixed(2))}`,
                    weatherLayersBytes: `${parseFloat(
                        (metrics.weatherLayersBytes / (1024 * 1024)).toFixed(2))}`,
                    weatherForecastBytes: `${parseFloat(
                        (metrics.weatherForecastBytes / (1024 * 1024)).toFixed(2))}`,
                    positionsApiBytes: `${parseFloat(
                        (metrics.positionsApiBytes / (1024 * 1024)).toFixed(2))}`,
                    notificationsApiBytes: `${parseFloat(
                        (metrics.notificationsApiBytes / (1024 * 1024)).toFixed(2))}`,
                    terminalsApiBytes: `${parseFloat(
                        (metrics.terminalsApiBytes / (1024 * 1024)).toFixed(2))}`,
                    portMemosApiBytes: `${parseFloat(
                        (metrics.portMemosApiBytes / (1024 * 1024)).toFixed(2))}`,
                    mapsApiBytes: `${parseFloat(
                        (metrics.mapsApiBytes / (1024 * 1024)).toFixed(2))}`, 
                    insightHubApiBytes: `${parseFloat((metrics.insightHubApiBytes / (1024 * 1024)).toFixed(2))}`,
                    encTileserverApiBytes: `${parseFloat((metrics.encTileserverApiBytes / (1024 * 1024)).toFixed(2))}`,
                    geofenceApiBytes: `${parseFloat((metrics.geofenceApiBytes / (1024 * 1024)).toFixed(2))}`,
                    configApiBytes: `${parseFloat((metrics.configApiBytes / (1024 * 1024)).toFixed(2))}`,
                    performanceApiBytes: `${parseFloat((metrics.performanceApiBytes / (1024 * 1024)).toFixed(2))}`,
                    threeMdsApiBytes: `${parseFloat((metrics.threeMdsApiBytes / (1024 * 1024)).toFixed(2))}`,
                    routeOptimiserApiBytes: `${parseFloat((metrics.routeOptimiserApiBytes / (1024 * 1024)).toFixed(2))}`,
                    onlineStatusApiBytes: `${parseFloat((metrics.onlineStatusApiBytes / (1024 * 1024)).toFixed(2))}`,
                    playForwardClicked: localStorage.getItem('play-clicked') || '0',
                    // Add validation metrics to help debug discrepancies
                    categorySumBytes: `${parseFloat((categorySum / (1024 * 1024)).toFixed(2))}`,
                    unmatchedBytes: `${parseFloat((unmatchedBytes / (1024 * 1024)).toFixed(2))}`,
                    hasDiscrepancy: `${Math.abs(unmatchedBytes) > 1024}` // Flag if >1KB difference
                }

                faro.api.pushEvent('daily-api-call-count', eventData);
                console.log("eventData >> ", eventData);
            }
        } catch (error) {
            console.error('Failed to push metrics to RUM:', error)
        }
    }

    private async clearAllImoMetrics(): Promise<void> {
        if (!this.db) {
            return
        }

        const transaction = this.db.transaction(STORE_NAME, 'readwrite')
        const store = transaction.objectStore(STORE_NAME)

        const getAllKeysRequest = store.getAllKeys()

        return new Promise((resolve, reject) => {
            getAllKeysRequest.onsuccess = () => {
                const keys = getAllKeysRequest.result

                // deleting all IMO metrics (but not global timing)
                const deletePromises: Promise<void>[] = []

                for (const key of keys) {
                    if (typeof key === 'string' && key.startsWith('imo-')) {
                        deletePromises.push(
                            new Promise<void>((delResolve, delReject) => {
                                const deleteRequest = store.delete(key)
                                deleteRequest.onsuccess = () => delResolve()
                                deleteRequest.onerror = () =>
                                    delReject(
                                        new Error(`Failed to delete ${key}`),
                                    )
                            }),
                        )
                    }
                }

                Promise.all(deletePromises)
                    .then(() => resolve())
                    .catch(reject)
            }

            getAllKeysRequest.onerror = () => {
                reject(new Error('Failed to get all keys for clearing'))
            }
        })
    }

    /**
     * Start interval to check and push metrics periodically
     * For production (24 hour interval): should check every hour
     */
    private startDailyPushInterval(): void {
        const checkInterval =  60 * 60 * 1000 // 1 hour 

        this.intervalId = window.setInterval(async () => {
            await this.checkAndPushIfNeeded()
        }, checkInterval)
    }

    stop(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId)
            this.intervalId = null
        }

        if (this.db) {
            this.db.close()
            this.db = null
        }

        // Reset initialization state to allow restart
        this.initializationPromise = null
    }
}

export const apiCallTracker = new ApiCallTracker()